// Plating cards (M16): the built-in cards plus the restaurant's own (IndexedDB 'epb-plating'), duplicate,
// edit, delete own cards, export / import a card file, and PNG / PDF for the kitchen wall.
import { create } from 'zustand';
import { createStore, get, set, type UseStore } from 'idb-keyval';
import { PDFDocument } from 'pdf-lib';
import { BUILT_IN_CARDS, type PlatingCard } from './cards';
import { CARD_H, CARD_W, drawCard, type CardLang } from './drawCard';
import { useClipboard } from '../../state/clipboard';

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-plating', 'cards'));
const KEY = 'mine';

type PlatingState = { mine: PlatingCard[]; loaded: boolean; selected: string; lang: CardLang };
export const usePlating = create<PlatingState>(() => ({
  mine: [],
  loaded: false,
  selected: BUILT_IN_CARDS[0]!.id,
  lang: 'es',
}));

export const allCards = (): PlatingCard[] => [...BUILT_IN_CARDS, ...usePlating.getState().mine];
export const cardById = (id: string) => allCards().find((c) => c.id === id);

export async function loadCards() {
  if (usePlating.getState().loaded) return;
  let mine: PlatingCard[] = [];
  try {
    mine = (await get<PlatingCard[]>(KEY, store())) ?? [];
  } catch {
    /* storage unavailable */
  }
  usePlating.setState({ mine, loaded: true });
}
/**
 * All own cards are one IndexedDB value (photos included). A drag or typing changes the card many times a
 * second, so writes are coalesced: the state changes at once, the write follows 400 ms after the last change
 * (or at once for duplicate / delete / import, and when the page is hidden).
 */
const WRITE_DELAY_MS = 400;
let pending: PlatingCard[] | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let warned = false;
export async function flushPlating() {
  if (timer) clearTimeout(timer);
  timer = null;
  const mine = pending;
  pending = null;
  if (!mine) return;
  try {
    await set(KEY, mine, store());
  } catch {
    // Storage unavailable or full: the cards last for this session. Say so once instead of losing them silently.
    if (!warned) useClipboard.getState().setToast({ key: 'platingNotSaved' });
    warned = true;
  }
}
async function persist(mine: PlatingCard[], now = true) {
  usePlating.setState({ mine });
  pending = mine;
  if (now) return flushPlating();
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void flushPlating(), WRITE_DELAY_MS);
}
if (typeof document !== 'undefined')
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushPlating();
  });

const newId = () => `mine-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;

/** A copy the restaurant can edit (named after a menu dish later). */
export async function duplicateCard(id: string): Promise<string | null> {
  const c = cardById(id);
  if (!c) return null;
  const copy: PlatingCard = {
    ...structuredClone(c),
    id: newId(),
    builtIn: false,
    name: { en: `${c.name.en} (copy)`, es: `${c.name.es} (copia)` },
    updatedAt: new Date().toISOString(),
  };
  await persist([...usePlating.getState().mine, copy]);
  usePlating.setState({ selected: copy.id });
  return copy.id;
}

export async function updateCard(id: string, fn: (c: PlatingCard) => void) {
  const mine = usePlating.getState().mine.map((c) => {
    if (c.id !== id) return c;
    // The photo is not cloned: it keeps its identity, so the preview does not decode it again on every edit
    // (a drag is an edit per pointer move). An edit that changes the photo replaces the object.
    const { photo, ...rest } = c;
    const n: PlatingCard = { ...structuredClone(rest), ...(photo ? { photo } : {}) };
    fn(n);
    n.updatedAt = new Date().toISOString();
    return n;
  });
  await persist(mine, false);
}

/** Deletes an own card at once; an Undo toast (8 s) puts it back in the same place (Your call Y5). */
export async function deleteCard(id: string) {
  const mine = usePlating.getState().mine;
  const index = mine.findIndex((c) => c.id === id);
  if (index < 0) return;
  const card = mine[index]!;
  await persist(mine.filter((c) => c.id !== id));
  usePlating.setState({ selected: BUILT_IN_CARDS[0]!.id });
  useClipboard.getState().setToast({
    key: 'platingDeleted',
    ms: 8000,
    action: { key: 'undo', run: () => void restoreCard(card, index) },
  });
}

/** Puts a deleted card back at its old position and selects it. */
export async function restoreCard(card: PlatingCard, index: number) {
  const mine = [...usePlating.getState().mine];
  if (mine.some((c) => c.id === card.id)) return;
  mine.splice(Math.min(index, mine.length), 0, card);
  await persist(mine);
  usePlating.setState({ selected: card.id });
}

// ───────────── files ─────────────

const toB64 = (u: Uint8Array) => {
  let s = '';
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode(...u.subarray(i, i + 0x8000));
  return btoa(s);
};

/** A card as a file (`.epbplate.json`), photo included. */
export function cardFile(c: PlatingCard): string {
  const { photo, ...rest } = c;
  return JSON.stringify(
    {
      app: 'eat-photo-booth',
      kind: 'plating-card',
      v: 1,
      card: { ...rest, photo: photo ? { type: photo.type, data: toB64(photo.bytes) } : null },
    },
    null,
    1,
  );
}

/** Reads a card file; throws on anything else. The imported card is a new own card. */
export async function importCardFile(text: string): Promise<string> {
  const raw = JSON.parse(text) as { app?: string; kind?: string; v?: number; card?: Record<string, unknown> };
  if (raw.app !== 'eat-photo-booth' || raw.kind !== 'plating-card' || !raw.card)
    throw new Error('not a plating card file');
  if ((raw.v ?? 0) > 1) throw new Error('made by a newer version of the app');
  const c = raw.card as unknown as PlatingCard & { photo: { type: string; data: string } | null };
  if (!c.name?.en || !Array.isArray(c.elements) || !Array.isArray(c.steps))
    throw new Error('card is incomplete');
  const card: PlatingCard = {
    ...c,
    id: newId(),
    builtIn: false,
    photo: c.photo
      ? { type: c.photo.type, bytes: Uint8Array.from(atob(c.photo.data), (ch) => ch.charCodeAt(0)) }
      : null,
  };
  await persist([...usePlating.getState().mine, card]);
  return card.id;
}

// ───────────── export ─────────────

async function photoBitmap(c: PlatingCard) {
  return c.photo
    ? createImageBitmap(new Blob([c.photo.bytes as Uint8Array<ArrayBuffer>], { type: c.photo.type }))
    : null;
}

/** The card as PNG, Letter proportions at `dpi` (150 → 1275 × 1650). */
export async function cardPng(c: PlatingCard, lang: CardLang, dpi = 150): Promise<Uint8Array> {
  const k = dpi / 150;
  const cv = new OffscreenCanvas(Math.round(CARD_W * k), Math.round(CARD_H * k));
  const photo = await photoBitmap(c);
  drawCard(cv.getContext('2d')!, c, lang, cv.width, cv.height, photo);
  photo?.close();
  return new Uint8Array(await (await cv.convertToBlob({ type: 'image/png' })).arrayBuffer());
}

/** Letter in pt, and the margin most office and kitchen printers can print inside (Your call Y9). */
export const LETTER_PT = { w: 612, h: 792 };
export const PRINT_MARGIN_PT = 18; // 0.25 in

/** Where the card goes on the Letter page: as large as fits inside the margin, same shape, centred. */
export function cardPlacement() {
  const k = Math.min(
    (LETTER_PT.w - 2 * PRINT_MARGIN_PT) / CARD_W,
    (LETTER_PT.h - 2 * PRINT_MARGIN_PT) / CARD_H,
  );
  const width = CARD_W * k;
  const height = CARD_H * k;
  return { x: (LETTER_PT.w - width) / 2, y: (LETTER_PT.h - height) / 2, width, height };
}

/**
 * The card as a one-page Letter PDF: the 200 dpi PNG inside a 0.25 in print-safe margin (it was full-bleed,
 * and most printers clipped the footer).
 */
export async function cardPdf(c: PlatingCard, lang: CardLang): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.setTitle(lang === 'en' ? c.name.en : c.name.es);
  pdf.setCreator('EAT Photo Booth');
  const page = pdf.addPage([LETTER_PT.w, LETTER_PT.h]);
  const img = await pdf.embedPng(await cardPng(c, lang, 200));
  page.drawImage(img, cardPlacement());
  return pdf.save();
}

/** Longest edge of an own card's reference photo: it prints about 2.4 in wide, so more is wasted storage. */
export const CARD_PHOTO_EDGE = 1000;

/**
 * A photo picked for an own card, shrunk to CARD_PHOTO_EDGE (Your call Y9): all own cards are one IndexedDB value,
 * so a 12 MP phone photo per card adds up. Smaller photos are kept as they are. JPEG stays JPEG; anything else
 * becomes PNG, which keeps transparency.
 */
export async function fitCardPhoto(photo: { bytes: Uint8Array; type: string }) {
  const bmp = await createImageBitmap(new Blob([photo.bytes as Uint8Array<ArrayBuffer>], { type: photo.type }));
  try {
    const k = CARD_PHOTO_EDGE / Math.max(bmp.width, bmp.height);
    if (k >= 1) return photo;
    const cv = new OffscreenCanvas(Math.max(1, Math.round(bmp.width * k)), Math.max(1, Math.round(bmp.height * k)));
    cv.getContext('2d')!.drawImage(bmp, 0, 0, cv.width, cv.height);
    const type = photo.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
    const blob = await cv.convertToBlob(type === 'image/jpeg' ? { type, quality: 0.9 } : { type });
    return { bytes: new Uint8Array(await blob.arrayBuffer()), type };
  } finally {
    bmp.close();
  }
}
